/**
 * @name Logo.tsx
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Componente Reutilizável de Logo com propriedades de largura e altura personalizáveis.
 * Agora carrega dinamicamente da configuração ou usa padrão local.
 * @example <Logo alt="Logo" width={200} height={200} />
 * @param {string} src - Caminho da imagem da logo (opcional, usa configuração se não fornecido).
 * @param {string} alt - Texto alternativo da imagem da logo.
 * @param {string | number} width - Largura da imagem da logo.
 * @param {string | number} height - Altura da imagem da logo.
 * @returns {React.FC<LogoProps>} - O JSX do componente.
 * @package src/components/utils/Logo
 **/

import { useLogo } from '@/hooks/useLogo';
import { styled } from '@mui/material';
import Box from '@mui/material/Box';
import React from 'react';
import { useNavigate } from 'react-router-dom';

const StyledLogo = styled('img')({
     display: 'block',
     maxWidth: '100%',
     height: 'auto',
     cursor: 'pointer', // Adiciona o cursor de ponteiro
     transition: 'transform 0.2s', // Adiciona uma transição suave
     '&:hover': {
          transform: 'scale(1.05)', // Aumenta ligeiramente o tamanho ao passar o mouse
     },
});

interface LogoProps {
     src?: string; // Agora é opcional
     alt?: string;
     width?: string | number;
     height?: string | number;
}

const Logo: React.FC<LogoProps> = ({ src, alt = 'Logo', width, height }) => {
     const navigate = useNavigate();
     const { currentLogo } = useLogo();

     // Usa src fornecido ou logo dinâmica da configuração
     const logoSrc = src || currentLogo;

     return (
          <Box
               component='div'
               sx={{
                    width: width || 'auto',
                    height: height || 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
               }}
          >
               <StyledLogo
                    src={logoSrc}
                    alt={alt}
                    style={{ width, height }}
                    onClick={() => navigate('/')}
               />
          </Box>
     );
};

export default Logo;

/**
 */
